import React from 'react';
import { Box, Typography, Chip, IconButton } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Euro as EuroIcon, Category as CategoryIcon } from '@mui/icons-material';

import ResponsiveTable from '../../../components/common/ResponsiveTable';
import ItemCard from '../../../components/common/ItemCard';

const ServiceTemplateTable = ({ 
  templates, 
  loading, 
  onEdit, 
  onDelete, 
  sortField, 
  sortDirection, 
  onSort 
}) => {
  // Mobile card renderer
  const renderTemplateCard = (template) => (
    <ItemCard
      key={template.id}
      title={template.name}
      subtitle={formatServiceType(template.service_type)}
      headerActions={[
        <Chip 
          key="status"
          label={template.is_active ? 'Ενεργό' : 'Ανενεργό'}
          color={template.is_active ? 'success' : 'default'}
          size="small"
        />,
        <Chip 
          key="price"
          label={formatPrice(template.default_price)}
          color="info"
          variant="outlined"
          size="small"
        />
      ]}
      basicInfo={[
        { icon: <EuroIcon fontSize="small" />, text: formatPrice(template.default_price) },
        { icon: <CategoryIcon fontSize="small" />, text: formatServiceType(template.service_type) },
        { icon: <CategoryIcon fontSize="small" />, text: formatBillingPeriod(template.billing_period) }
      ].filter(info => info.text)}
      actions={[
        <IconButton
          key="edit"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(template);
          }}
          sx={{ color: 'primary.main' }}
        >
          <EditIcon fontSize="small" />
        </IconButton>,
        <IconButton
          key="delete"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(template);
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      ]}
    />
  );

  // Helper functions
  const formatServiceType = (type) => {
    const types = {
      'development': 'Ανάπτυξη',
      'hosting': 'Hosting',
      'maintenance': 'Συντήρηση',
      'seo': 'SEO',
      'design': 'Σχεδιασμός',
      'other': 'Άλλο'
    };
    return types[type] || type;
  };

  const formatBillingPeriod = (period) => {
    const periods = {
      'one_time': 'Μία φορά',
      'monthly': 'Μηνιαία',
      'quarterly': 'Τριμηνιαία',
      'semi_annual': 'Εξαμηνιαία',
      'annual': 'Ετήσια',
      'biennial': 'Διετής'
    };
    return periods[period] || period;
  };

  const formatPrice = (price) => {
    return `€${parseFloat(price).toFixed(2)}`;
  };

  // Table columns configuration
  const columns = [
    {
      field: 'name',
      headerName: 'Όνομα Προτύπου',
      sortable: true,
      minWidth: 200,
      renderCell: (value) => (
        <Typography variant="body2" fontWeight={500}>
          {value}
        </Typography>
      )
    },
    {
      field: 'service_type',
      headerName: 'Τύπος',
      sortable: true,
      minWidth: 120,
      renderCell: (value) => (
        <Chip
          label={formatServiceType(value)}
          color="info"
          variant="outlined"
          size="small"
        />
      )
    },
    {
      field: 'default_price',
      headerName: 'Τιμή',
      sortable: true,
      minWidth: 100,
      renderCell: (value) => (
        <Typography variant="body2" fontWeight={500}>
          {formatPrice(value)}
        </Typography>
      )
    },
    {
      field: 'billing_period',
      headerName: 'Περίοδος',
      sortable: true,
      minWidth: 120,
      renderCell: (value) => (
        <Chip
          label={formatBillingPeriod(value)}
          color="secondary"
          variant="outlined"
          size="small"
        />
      )
    },
    {
      field: 'is_active',
      headerName: 'Κατάσταση',
      sortable: true,
      minWidth: 120,
      renderCell: (value) => (
        <Chip
          label={value ? 'Ενεργό' : 'Ανενεργό'}
          color={value ? 'success' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Ενέργειες',
      sortable: false,
      minWidth: 100,
      renderCell: (value, row) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => onEdit(row)}
            sx={{ color: 'primary.main' }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onDelete(row)}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      )
    }
  ];

  return (
    <ResponsiveTable
      data={templates}
      columns={columns}
      loading={loading}
      emptyMessage="Δεν βρέθηκαν πρότυπα υπηρεσιών"
      sortField={sortField}
      sortDirection={sortDirection}
      onSort={onSort}
      renderMobileCard={renderTemplateCard}
    />
  );
};

export default ServiceTemplateTable;
